import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import MarketingShell from "../../components/MarketingShell";
import PageMeta from "../../components/PageMeta";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";
import { getPost, POSTS } from "./posts";

const FORMATTER = new Intl.DateTimeFormat("en-IN", { day: "numeric", month: "long", year: "numeric" });

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;

  // Recommend up to 2 other posts
  const others = POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <MarketingShell>
      <PageMeta
        title={`${post.title} · HeDesk Blog`}
        description={post.excerpt}
        canonical={`https://hedesk.help/blog/${post.slug}`}
      />
      <Link to="/blog" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-8">
        <ArrowLeft className="w-3.5 h-3.5" /> Back to all posts
      </Link>

      <p className="text-xs uppercase tracking-[0.2em] text-blue-400 font-semibold">{post.tag}</p>
      <h1 className="mt-3 text-3xl sm:text-5xl font-bold text-white tracking-tight leading-[1.1]" style={{ fontFamily: "Manrope" }}>
        {post.title}
      </h1>
      <p className="mt-4 text-base sm:text-lg text-slate-400 leading-relaxed">{post.excerpt}</p>

      <div className="mt-6 flex items-center gap-4 text-xs text-slate-500">
        <span>{FORMATTER.format(new Date(post.date))}</span>
        <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read} read</span>
        <span>by {post.author}</span>
      </div>

      <div className={`mt-10 aspect-[16/8] rounded-3xl bg-gradient-to-br ${post.cover} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/40" />
      </div>

      <article className="mt-10">
        <post.body />
      </article>

      {others.length > 0 && (
        <section className="mt-20 pt-10 border-t border-white/10">
          <h2 className="text-sm uppercase tracking-[0.2em] text-blue-400 font-semibold mb-5">Continue reading</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {others.map((o) => (
              <Link
                key={o.slug}
                to={`/blog/${o.slug}`}
                className="group p-5 rounded-2xl border border-white/10 bg-white/[0.03] hover:bg-white/[0.05] transition-all"
              >
                <p className="text-xs uppercase tracking-wider text-blue-400 font-semibold">{o.tag}</p>
                <h3 className="mt-1 text-base font-semibold text-white group-hover:text-blue-300" style={{ fontFamily: "Manrope" }}>
                  {o.title}
                </h3>
                <p className="mt-1 text-sm text-slate-400 line-clamp-2">{o.excerpt}</p>
                <div className="mt-3 inline-flex items-center gap-1 text-xs text-blue-400">
                  Read <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </MarketingShell>
  );
}
